"use client";

import Context from "component/design-system/react-responsive/Context";
import toQuery, { hyphenate } from "component/design-system/react-responsive/toQuery";
import { MediaQueryAllQueryable, MediaQueryMatchers } from "component/design-system/react-responsive/types";
import matchMedia from "matchmediaquery";
import {
  useContext, useEffect, useRef, useState,
} from "react";
// import { shallowEqualObjects } from "shallow-equal";

type MediaQuerySettings = Partial<MediaQueryAllQueryable & { query?: string }>;
type HyphenateKeyTypes = MediaQueryMatchers | MediaQueryAllQueryable;

const makeQuery = (settings: MediaQuerySettings) => settings.query || toQuery(settings);

function shallowEqualObjects(obj1, obj2) {
  if (typeof obj1 !== "object" || obj1 === null) {
    return false;
  }
  if (typeof obj2 !== "object" || obj2 === null) {
    return false;
  }
  return Object.keys(obj1).length === Object.keys(obj2).length
  && Object.keys(obj1).every((key) => obj1[key] === obj2[key]);
}

const hyphenateKeys = (obj?: HyphenateKeyTypes) => {
  type K = keyof HyphenateKeyTypes;

  if (!obj) return undefined;
  const keys = Object.keys(obj) as K[];

  return keys.reduce((result, key) => {
    // eslint-disable-next-line no-param-reassign
    result[hyphenate(key)] = obj[key];
    return result;
  }, {} as Record<string, typeof obj[K]>);
};

const useIsUpdate = () => {
  const ref = useRef(false);

  useEffect(() => {
    ref.current = true;
  }, []);

  return ref.current;
};

const useDevice = (deviceFromProps?: MediaQueryMatchers): Partial<MediaQueryAllQueryable> | undefined => {
  const deviceFromContext = useContext(Context);
  const getDevice = () => hyphenateKeys(deviceFromProps) || hyphenateKeys(deviceFromContext);
  const [device, setDevice] = useState(getDevice);

  useEffect(() => {
    const newDevice = getDevice();
    if (!shallowEqualObjects(device, newDevice)) {
      setDevice(newDevice);
    }
  }, [deviceFromProps, deviceFromContext]);

  return device;
};

const useQuery = (settings: MediaQuerySettings) => {
  const getQuery = () => makeQuery(settings);
  const [query, setQuery] = useState(getQuery);

  useEffect(() => {
    const newQuery = getQuery();
    if (query !== newQuery) {
      setQuery(newQuery);
    }
  }, [settings]);

  return query;
};

const useMatchMedia = (query: string, device?: MediaQueryMatchers) => {
  const getMatchMedia = () => matchMedia(query, device || {}, !!device);
  const [mq, setMq] = useState(getMatchMedia);
  const isUpdate = useIsUpdate();

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isUpdate) {
      // skip on mounting, it has already been set
      const newMq = getMatchMedia();
      setMq(newMq);

      return () => {
        if (newMq) {
          newMq.dispose();
        }
      };
    }
  }, [query, device]);

  return mq;
};

const useMatches = (mediaQuery: MediaQueryList): boolean => {
  const [matches, setMatches] = useState<boolean>(mediaQuery.matches);

  useEffect(() => {
    const updateMatches = (ev: MediaQueryListEvent) => {
      setMatches(ev.matches);
    };
    mediaQuery.addListener(updateMatches);
    setMatches(mediaQuery.matches);

    return () => {
      mediaQuery.removeListener(updateMatches);
    };
  }, [mediaQuery]);

  return matches;
};

const useMediaQuery = (settings: MediaQuerySettings, device?: MediaQueryMatchers, onChange?: (_: boolean) => void) => {
  const deviceSettings = useDevice(device);
  const query = useQuery(settings);
  if (!query) throw new Error("Invalid or missing MediaQuery!");
  const mq = useMatchMedia(query, deviceSettings);
  const matches = useMatches(mq as unknown as MediaQueryList);
  const isUpdate = useIsUpdate();

  useEffect(() => {
    if (isUpdate && onChange) {
      onChange(matches);
    }
  }, [matches]);

  useEffect(() => () => {
    if (mq) {
      mq.dispose();
    }
  }, []);

  return matches;
};

export default useMediaQuery;
